// WHAT: Main application component for roadmap management with tab navigation
// WHY:  Provides the primary interface for roadmap features with React Router integration
// DOMAIN: Admin → Roadmap
// DEPENDENCIES: React Router, RoadmapProvider context, EditModal component
// RISKS:
//   - NOTE: Keyboard shortcuts are global - may conflict with other features
//   - NOTE: Tab navigation uses path matching - ensure routes are correctly configured
//   - NOTE: Modal state is local - consider using context for global modal management

import { BarChart3, LucidePlus } from "lucide-react";
import { useState, useCallback, useRef, useEffect } from "react";
import { useLocation, useNavigate, Outlet } from "react-router-dom";

import { Card, Button } from "@/components/ui";

import { RoadmapProvider } from "./context/useRoadmapContext";
import { EditModal } from "./screen/Overview/components/molecules/EditModal";

/**
 * WHAT:
 *   Main application component for roadmap management with tab-based navigation
 *   and keyboard shortcuts.
 *
 * WHY:
 *   Serves as the entry point for the roadmap feature, providing navigation,
 *   context, and modal management for all roadmap-related functionality.
 *
 * BEHAVIOR:
 *   - Wraps application in RoadmapProvider for global state management
 *   - Provides tab navigation (Overview, Timeline, Analytics, Matrix, Kanban)
 *   - Manages create modal state
 *   - Handles keyboard shortcuts (Ctrl/Cmd+N for new item)
 *   - Renders tab content via React Router Outlet
 *
 * @returns {JSX.Element} Die gerenderte RoadmapApp-Komponente
 *
 * RISKS:
 *   - NOTE: Keyboard shortcuts are document-level - may conflict with browser shortcuts
 *   - NOTE: Tab matching uses exact path - ensure routes match tab paths exactly
 *   - NOTE: Modal state is component-level - consider global modal state management
 *   - NOTE: createButtonRef is created but not used - may be for future focus management
 *
 */
export const RoadmapApp: React.FC = () => {
  // NOTE: React Router hooks for navigation
  const location = useLocation();
  const navigate = useNavigate();
  // NOTE: State for create modal visibility
  const [showCreateModal, setShowCreateModal] = useState(false);
  // NOTE: Ref for create button (currently unused but available for focus management)
  const createButtonReference = useRef<HTMLButtonElement>(null);

  // NOTE: Tab configuration array - defines all available roadmap views
  const tabs = [
    {
      description: "Roadmap Overview, Module Stats & Top 3 ROI Items",
      id: "overview",
      label: "Overview",
      path: "/roadmap",
    },
    {
      description: "Gantt-Chart Ansicht der Roadmap",
      id: "timeline",
      label: "Timeline",
      path: "/roadmap/timeline",
    },
    {
      description: "Charts & Metriken der Roadmap",
      id: "analytics",
      label: "Analytics",
      path: "/roadmap/analytics",
    },
    {
      description: "Impact vs Effort Matrix",
      id: "matrix",
      label: "Priority Matrix",
      path: "/roadmap/matrix",
    },
    {
      description: "Status-basierte Übersicht aller Items",
      id: "kanban",
      label: "Kanban Board",
      path: "/roadmap/kanban",
    },
  ];

  // NOTE: Find current tab based on location pathname, default to first tab
  const currentTab = tabs.find((tab) => location.pathname === tab.path) ?? tabs[0];

  /**
   * WHAT:
   *   Handles tab navigation by navigating to the selected tab's path.
   *
   * WHY:
   *   Centralizes navigation logic for tab switching.
   *
   * BEHAVIOR:
   *   - Navigates to the specified path using React Router
   *
   * @param path
   *
   */
  const handleTabClick = (path: string) => {
    const result = navigate(path);
    if (result instanceof Promise) {
      result.catch((error: unknown) => {
        // eslint-disable-next-line no-console -- Reason: Navigation errors should be logged for debugging
        console.error("Navigation error:", error);
      });
    }
  };

  /**
   * WHAT:
   *   Handles keyboard shortcuts for roadmap operations.
   *
   * WHY:
   *   Provides keyboard accessibility for common operations (e.g., Ctrl+N for new item).
   *
   * BEHAVIOR:
   *   - Listens for Ctrl/Cmd+N to open create modal
   *   - Prevents default browser behavior for handled shortcuts
   *
   * RISKS:
   *   - NOTE: Global keyboard listener - may conflict with other features
   *   - NOTE: Only handles Ctrl/Cmd+N - other shortcuts mentioned in UI are not implemented
   */
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (
      (event.ctrlKey || event.metaKey) &&
      !event.shiftKey &&
      !event.altKey &&
      event.key.toLowerCase() === "n"
    ) {
      event.preventDefault();
      setShowCreateModal(true);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <RoadmapProvider>
      <div className="min-h-screen bg-slate-900">
        <div className="max-w-7xl mx-auto p-6 space-y-6">
          {/* TracQ Header */}
          <Card
            className="border-tracq-deep-blue/30 bg-gradient-to-r from-slate-800/50 to-slate-700/30"
            padding="lg"
            variant="default"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-tracq bg-gradient-to-br from-tracq-deep-blue to-tracq-bright-blue shadow-tracq-card">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">Roadmap</h1>
                  <p className="text-slate-300 text-sm">{currentTab?.description ?? ""}</p>
                  {/* Keyboard shortcuts help */}
                  <div className="text-xs text-slate-400 space-x-4 mt-1">
                    <span>Ctrl/Cmd+N: New Item</span>
                    <span>Ctrl/Cmd+F: Search</span>
                    <span>↑↓←→: Navigate</span>
                    <span>Enter: Edit</span>
                  </div>
                </div>
              </div>

              <Button
                aria-label="Create new roadmap item (Ctrl+N or Cmd+N)"
                className="shadow-tracq-card"
                onClick={() => {
                  setShowCreateModal(true);
                }}
                ref={createButtonReference}
                size="lg"
                variant="primary"
              >
                <LucidePlus className="w-5 h-5 mr-2" />
                New Item
              </Button>
            </div>
          </Card>

          {/* Tab Navigation */}
          <nav className="border-b border-slate-700">
            <div className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  className={`py-3 px-1 border-b-2 font-medium text-sm transition-all duration-200 ${
                    currentTab && currentTab.id === tab.id
                      ? "border-tracq-bright-blue text-tracq-bright-blue"
                      : "border-transparent text-slate-400 hover:text-slate-300 hover:border-slate-600"
                  }`}
                  key={tab.id}
                  onClick={() => {
                    handleTabClick(tab.path);
                  }}
                  title={tab.description}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </nav>

          {/* Tab Content */}
          <div className="space-y-6">
            <Outlet />
          </div>
        </div>

        {/* Modal Management */}
        {showCreateModal ? (
          <EditModal
            onClose={() => {
              setShowCreateModal(false);
            }}
          />
        ) : null}
      </div>
    </RoadmapProvider>
  );
};
